import { LiveAvatarSession, SessionEvent, SessionState, AgentEventsEnum } from '@heygen/liveavatar-web-sdk';

type SpeakerName = string;

class AvatarManager {
  sessions: Record<SpeakerName, LiveAvatarSession> = {};
  videoElements: Record<SpeakerName, HTMLVideoElement> = {};
  states: Record<SpeakerName, string> = {};
  globalQueue: Promise<void> = Promise.resolve();
  keepAliveTimers: Record<SpeakerName, ReturnType<typeof setInterval>> = {};

  async initialize(onProgress?: (connected: number, total: number) => void, personaIds?: { hostId?: string; debaterAId?: string; debaterBId?: string }): Promise<number> {
    const resp = await fetch('/api/liveavatar/tokens', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(personaIds || {}),
    });
    if (!resp.ok) throw new Error(`Token fetch failed: ${resp.status}`);
    const { tokens } = await resp.json() as { tokens: Record<string, { session_token?: string; error?: string }> };

    const names = Object.keys(tokens);
    let connected = 0;

    const sessionPromises = names.map((name) => {
      return new Promise<void>((resolve) => {
        const tokenData = tokens[name];
        if (tokenData.error) {
          console.warn(`[Avatar] ${name} token error:`, tokenData.error);
          this.states[name] = 'ERROR';
          resolve();
          return;
        }

        const session = new LiveAvatarSession(tokenData.session_token!);

        this.sessions[name] = session;
        this.states[name] = 'INITIALIZING';

        const timeout = setTimeout(() => {
          console.warn(`[Avatar] ${name} timed out connecting`);
          this.states[name] = 'TIMEOUT';
          resolve();
        }, 60000);

        session.on(SessionEvent.SESSION_STATE_CHANGED, (state: string) => {
          console.log(`[Avatar] ${name} state: ${state}`);
          this.states[name] = state;
          if (state === SessionState.CONNECTED) {
            connected++;
            onProgress?.(connected, names.length);
            clearTimeout(timeout);
            resolve();
          }
        });

        session.on(SessionEvent.SESSION_STREAM_READY, () => {
          console.log(`[Avatar] ${name} stream ready`);
          const videoEl = this.videoElements[name];
          if (videoEl) {
            session.attach(videoEl);
          }
        });

        session.start().catch((err: unknown) => {
          console.error(`[Avatar] ${name} failed to start:`, err);
          this.states[name] = 'ERROR';
          clearTimeout(timeout);
          resolve();
        });
      });
    });

    await Promise.all(sessionPromises);

    for (const name of names) {
      if (this.states[name] === SessionState.CONNECTED) {
        this.keepAliveTimers[name] = setInterval(() => {
          try { this.sessions[name]?.keepAlive(); } catch { /* ignore */ }
        }, 30000);
      }
    }

    return connected;
  }

  registerVideoElement(name: string, videoEl: HTMLVideoElement | null): void {
    if (videoEl) this.videoElements[name] = videoEl;
  }

  attachAll(): void {
    for (const [name, session] of Object.entries(this.sessions)) {
      if (this.states[name] === SessionState.CONNECTED) {
        const videoEl = this.videoElements[name];
        if (videoEl) {
          session.attach(videoEl);
          videoEl.classList.add('active');
        }
      }
    }
  }

  speak(name: string, text: string): Promise<void> {
    const session = this.sessions[name];
    if (!session || this.states[name] !== SessionState.CONNECTED) {
      return Promise.resolve();
    }

    const current = this.globalQueue.then(() => this._doSpeak(name, text));
    this.globalQueue = current.catch(() => {});
    return current;
  }

  _doSpeak(name: string, text: string): Promise<void> {
    return new Promise((resolve) => {
      const session = this.sessions[name];
      if (!session) { resolve(); return; }

      for (const [n, el] of Object.entries(this.videoElements)) {
        if (el) el.muted = (n !== name);
      }

      const videoEl = this.videoElements[name];
      if (videoEl) videoEl.classList.add('speaking');

      let resolved = false;
      const cleanup = () => {
        if (resolved) return;
        resolved = true;
        clearTimeout(safetyTimeout);
        session.off(AgentEventsEnum.AVATAR_SPEAK_ENDED, onSpeakEnd);
        if (videoEl) videoEl.classList.remove('speaking');
        if (videoEl) videoEl.muted = true;
        resolve();
      };

      const onSpeakEnd = () => {
        setTimeout(cleanup, 800);
      };
      session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, onSpeakEnd);

      const safetyTimeout = setTimeout(cleanup, 30000);

      try {
        session.repeat(text);
      } catch (err) {
        console.error(`[Avatar] ${name} speak failed:`, err);
        cleanup();
      }
    });
  }

  async destroy(): Promise<void> {
    for (const timer of Object.values(this.keepAliveTimers)) {
      clearInterval(timer);
    }
    this.keepAliveTimers = {};

    const stopPromises = Object.entries(this.sessions).map(async ([name, session]) => {
      try {
        await session.stop();
      } catch (e) {
        console.warn(`[Avatar] ${name} stop failed:`, e);
      }
    });
    await Promise.all(stopPromises);

    this.sessions = {};
    this.states = {};
    this.globalQueue = Promise.resolve();
  }
}

export const avatarManager = new AvatarManager();
